import {
  AnalyzeAttachmentRequest,
  AnalyzeAttachmentResponse,
  AnalyzeTempAttachmentRequest,
  AnalyzeEventAttachmentRequest,
} from '../types/analysis'
import { fetchWithAuth } from '../authFetcher'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

// Function to analyze any type of attachment
export const analyzeAttachment = async (
  request: AnalyzeAttachmentRequest
): Promise<AnalyzeAttachmentResponse> => {
  try {
    const data = await fetchWithAuth(`${URL}/events/analyze-attachments-bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })

    return data
  } catch (error) {
    console.error('Error analyzing attachment:', error)

    // For development purposes, return a mock response
    // In production, this would be removed
    return mockAnalyzeResponse(request)
  }
}

// Helper for analyzing an event attachment
export const analyzeEventAttachment = async (
  eventId: string,
  attachmentId: string | string[]
): Promise<AnalyzeAttachmentResponse> => {
  // Convert single attachment ID to array if needed
  const attachment_ids = Array.isArray(attachmentId)
    ? attachmentId
    : [attachmentId]

  return analyzeAttachment({
    attachment_type: 'event',
    event_id: eventId,
    attachment_ids,
  })
}

// Helper for analyzing a temporary attachment
export const analyzeTempAttachment = async (
  attachmentId: string | string[],
  createEvent?: boolean
): Promise<AnalyzeAttachmentResponse> => {
  // Convert single attachment ID to array if needed
  const attachment_ids = Array.isArray(attachmentId)
    ? attachmentId
    : [attachmentId]

  return analyzeAttachment({
    attachment_type: 'temp',
    attachment_ids,
    create_event: createEvent,
  })
}

// Mock response for development/testing
function mockAnalyzeResponse(
  request: AnalyzeAttachmentRequest
): AnalyzeAttachmentResponse {
  const isTemp = request.attachment_type === 'temp'

  // Get the first attachment ID (or the only one) from the array
  const attachmentIds = isTemp
    ? (request as AnalyzeTempAttachmentRequest).attachment_ids
    : (request as AnalyzeEventAttachmentRequest).attachment_ids

  // Use the first attachment ID for the response
  const attachmentId =
    attachmentIds && attachmentIds.length > 0 ? attachmentIds[0] : 'mock-id'

  const createEvent = isTemp
    ? (request as AnalyzeTempAttachmentRequest).create_event
    : false

  const response: AnalyzeAttachmentResponse = {
    success: true,
    analyzed_at: new Date().toISOString(),
    source: {
      attachment_id: attachmentId,
      file_name: `${isTemp ? 'temp_' : ''}file_${attachmentId.substring(
        0,
        6
      )}.pdf`,
      content_type: 'application/pdf',
      is_temp: isTemp,
      event_id: isTemp
        ? undefined
        : (request as AnalyzeEventAttachmentRequest).event_id,
    },
    event_details: {
      name: 'Career Day: Industry Professionals Needed',
      description:
        'Join us for our annual Career Day event where students can learn about various professions from industry experts.',
      expectation_statement:
        'We need professionals to give a 20-minute presentation about their career path and answer questions from students. A computer and projector will be available.',
      accomplish_statement:
        'We are organizing a career day for our students and looking for professionals who can share their experiences and insights about their careers.',
      keywords: [
        'career day',
        'education',
        'professional development',
        'networking',
      ],
      grade_levels: ['9-10', '11-12'],
      content_areas: ['Career Development', 'Professional Skills'],
      engagement_opportunities: [
        'Share career journey and answer questions',
        'Provide content/subject consultation with a class',
      ],
      maximum_attendees: 30,
      dates: [
        {
          startDate: '2024-05-15T09:00:00Z',
          endDate: '2024-05-15T15:00:00Z',
        },
      ],
      location: {
        name: 'Westside High School',
        address: '123 School Street',
        city: 'Springfield',
        state: 'IL',
        zip: '62701',
        virtual: false,
      },
      flexible_dates: false,
      flexible_times: true,
      flexible_location: false,
    },
  }

  // Add created_event data if createEvent flag was set
  if (createEvent) {
    response.created_event = {
      id: `event-${Math.random().toString(36).substring(2, 10)}`,
      name: response.event_details.name,
      status: 'DRAFT',
    }
  }

  return response
}
